import React, { useLayoutEffect, useMemo, useRef, useState } from 'react'
import {
    createResizeObserver,
    ElementInfo,
    extractElementInfo,
    SHIMMER_CONTAINER_STYLES
} from '@shimmer-from-structure/core'

export type StaticStructureShimmerProps = {
    children: React.ReactNode
    loading?: boolean
    backgroundColor?: string
    fallbackBorderRadius?: number
    templateProps?: Record<string, unknown>
    /** Stretch to fill the parent height (e.g. sidebar panels). */
    fill?: boolean
}

const fillContainerStyle: React.CSSProperties = {
    height: '100%',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column'
}

/**
 * Structure-aware loading placeholder without animated gradient sweep.
 * Measures child layout and fills matching regions with a flat background color.
 */
const StaticStructureShimmer: React.FC<StaticStructureShimmerProps> = ({
    children,
    loading = true,
    backgroundColor = 'rgba(0, 0, 0, 0.06)',
    fallbackBorderRadius = 4,
    templateProps,
    fill = false
}) => {
    const [elements, setElements] = useState<ElementInfo[]>([])
    const measureRef = useRef<HTMLDivElement>(null)

    const measuredChildren = useMemo(() => {
        if (!loading || !templateProps) {
            return children
        }
        const childList = React.Children.toArray(children)
        if (childList.length === 0) {
            return children
        }
        const first = childList[0]
        if (!React.isValidElement(first)) {
            return children
        }
        return [React.cloneElement(first, { ...templateProps }), ...childList.slice(1)]
    }, [children, loading, templateProps])

    useLayoutEffect(() => {
        if (!loading || !measureRef.current) {
            return
        }

        const container = measureRef.current
        const measure = () => {
            try {
                const rect = container.getBoundingClientRect()
                const info: ElementInfo[] = []
                Array.from(container.children).forEach((child) => {
                    info.push(...extractElementInfo(child as HTMLElement, rect))
                })
                const containerArea = rect.width * rect.height
                const filtered =
                    fill && containerArea > 0
                        ? info.filter((el) => el.width * el.height < containerArea * 0.85)
                        : info
                setElements(filtered)
            } catch (error) {
                console.error('Error measuring static shimmer elements:', error)
            }
        }

        measure()
        return createResizeObserver(container, measure)
    }, [loading, measuredChildren])

    if (!loading) {
        return <>{children}</>
    }

    const containerStyle: React.CSSProperties = fill
        ? { position: 'relative', ...fillContainerStyle }
        : { position: 'relative' }
    const measureStyle: React.CSSProperties = fill
        ? { pointerEvents: 'none', ...fillContainerStyle }
        : { pointerEvents: 'none' }

    return (
        <div style={containerStyle}>
            <style>{SHIMMER_CONTAINER_STYLES}</style>
            <div ref={measureRef} className="shimmer-measure-container" style={measureStyle} aria-hidden="true">
                {measuredChildren}
            </div>
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflow: 'hidden'
                }}
            >
                {elements.map((element, index) => (
                    <div
                        key={index}
                        style={{
                            position: 'absolute',
                            left: `${element.x}px`,
                            top: `${element.y}px`,
                            width: `${element.width}px`,
                            height: `${element.height}px`,
                            backgroundColor,
                            borderRadius:
                                element.borderRadius === '0px'
                                    ? `${fallbackBorderRadius}px`
                                    : element.borderRadius
                        }}
                    />
                ))}
            </div>
        </div>
    )
}

export default StaticStructureShimmer
